import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, Clock, Users } from "lucide-react";

/**
 * Sticky trust strip that sits directly under the navbar on every page.
 * Shows live community signals: codes verified this week, work-rate %, freshness.
 * Fails silently if the endpoint is unreachable — the bar simply doesn't render.
 */
export default function TrustBar() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/trust/stats");
        if (mounted) setStats(data);
      } catch (err) {
        console.debug("Trust stats unavailable:", err?.message);
      }
    })();
    // Refresh every 5 min so the "N min ago" doesn't get stale
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get("/trust/stats");
        if (mounted) setStats(data);
      } catch {
        /* silent */
      }
    }, 5 * 60 * 1000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  if (!stats) return null;

  const {
    verified_this_week: verified,
    work_rate_pct: workRate,
    last_verified_minutes_ago: freshMin,
    active_clippers_this_week: clippers,
  } = stats;

  const freshLabel = freshMin == null ? "just now" : freshMin < 60
    ? `${freshMin} min ago`
    : freshMin < 60 * 24
      ? `${Math.round(freshMin / 60)}h ago`
      : `${Math.round(freshMin / (60 * 24))}d ago`;

  return (
    <div
      data-testid="trust-bar"
      className="w-full border-b-2 border-[#2d2a32]/70 bg-[#2d2a32] text-[#f6f4f0]"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-2 flex items-center justify-between gap-4 text-[11px] md:text-xs mono tracking-[0.15em] uppercase overflow-x-auto">
        <div className="flex items-center gap-1.5 shrink-0" data-testid="trust-verified">
          <ShieldCheck className="w-3.5 h-3.5 text-[#4ecdc4]" />
          <span className="opacity-80">Verified this week ·</span>
          <span className="font-bold text-white">{verified.toLocaleString()}</span>
        </div>

        <div className="hidden md:flex items-center gap-1.5 shrink-0" data-testid="trust-workrate">
          <span className="w-2 h-2 rounded-full bg-[#7dbb6e] animate-pulse" />
          <span className="opacity-80">Community work-rate ·</span>
          <span className="font-bold text-white">{workRate}%</span>
        </div>

        <div className="flex items-center gap-1.5 shrink-0" data-testid="trust-fresh">
          <Clock className="w-3.5 h-3.5 text-[#f7c873]" />
          <span className="opacity-80">Last verified ·</span>
          <span className="font-bold text-white">{freshLabel}</span>
        </div>

        {clippers > 0 && (
          <div className="hidden lg:flex items-center gap-1.5 shrink-0" data-testid="trust-clippers">
            <Users className="w-3.5 h-3.5 text-[#ff6b6b]" />
            <span className="opacity-80">Active clippers ·</span>
            <span className="font-bold text-white">{clippers.toLocaleString()}</span>
          </div>
        )}
      </div>
    </div>
  );
}
